import {inject, observer} from 'mobx-react';
import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {Button} from '../../../Button';
import {FormattedMessage} from 'components/FormattedComponents';
import InfoModal from 'components/InfoModal';
import MediaUploader from 'components/MediaUploader';
import {withSidePanel} from 'components/SidePanel';
import ModalsTextBox from 'components/ModalsTextBox';

import {maxFeedbackLength} from './constants';
import {ContentWrapper, MediaUploaderWrapper, Description, ButtonsContainer, Container, Label} from './styles';
import messages from './messages';
import {debounce} from 'lodash';

@withSidePanel
@inject('store')
@observer
class CheckFeedbackModal extends Component {
  state = {canNavigateAway: true, isDesktop: true};
  dropzoneRef = null;
  mediaUploaderRef = null;
  resolveImageRemoval = null;
  rejectImageRemoval = null;
  resizeListener = null;

  componentDidMount() {
    const {stepId, store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.init(stepId);

    this.resizeListener = window.addEventListener('resize', debounce(this.handleResize, 100));
    this.handleResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeListener);
  }

  handleResize = () => {
    const isDesktop = window.innerWidth >= 768;
    if (isDesktop !== this.state.isDesktop) {
      this.setState({isDesktop});
    }
  };

  handleMediaUploaderUploadStart = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setIsUploading(true);
  };

  handleMediaUploaderUploadEnd = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setIsUploading(false);
  };

  handleMediaUploaderBeforeRemove = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    return new Promise((resolve, reject) => {
      this.resolveImageRemoval = resolve;
      this.rejectImageRemoval = reject;
      dialog.setIsRemoveImageDialogOpen(true);
    });
  };

  handleMediaUploaderChange = mediaUrl => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setMediaUrl(mediaUrl);
  };

  handleMediaUploaderError = ({type, data}) => {
    const {intl, store} = this.props;
    const {formatMessage} = intl;
    const {checklist: checklistStore, feedbackDialog: dialog} = store;

    if (type === 'networkError') {
      dialog.setIsTryAgainErrorDialogOpen(true);
    } else {
      dialog.setMediaUploadErrorText(formatMessage(messages[type] || messages.fileNotSupported, data));
      checklistStore.openErrorModal();
    }
  };

  handleTextareaChange = event => {
    const {
      store: {
        feedbackDialog: {setFeedbackText}
      }
    } = this.props;
    const feedbackText = event.target.value;

    if (feedbackText.length <= maxFeedbackLength) {
      setFeedbackText(event.target.value);
    }
  };

  handleFormSubmit = event => {
    const {stepId, store} = this.props;
    const {checklist: checklistStore, feedbackDialog: dialog} = store;
    const {feedbackText, isUploading, mediaUrl} = dialog;

    event.preventDefault();

    if (isUploading) {
      dialog.setIsSaveWithoutNewImageDialogOpen(true);
    } else {
      checklistStore.updateChecklist(stepId, {feedbackText, mediaUrl});
      this.props.popSidePanel();
    }
  };

  handleSaveWithoutNewImageDialogClose = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setIsSaveWithoutNewImageDialogOpen(false);
  };

  handleSaveWithoutNewImageDialogConfirm = () => {
    const {stepId, store} = this.props;
    const {checklist: checklistStore, feedbackDialog: dialog} = store;
    const {feedbackText} = dialog;

    checklistStore.updateChecklist(stepId, {feedbackText});

    dialog.setIsSaveWithoutNewImageDialogOpen(false);
    dialog.setIsUploading(false);
    this.props.popSidePanel();
  };

  handleTryAgainErrorDialogClose = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setIsTryAgainErrorDialogOpen(false);
  };

  handleTryAgainErrorDialogConfirm = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setIsTryAgainErrorDialogOpen(false);

    if (this.mediaUploaderRef && this.mediaUploaderRef.wrappedInstance) {
      this.mediaUploaderRef.wrappedInstance.retry();
    }
  };

  handleRemoveImageDialogConfirm = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setIsRemoveImageDialogOpen(false);

    if (typeof this.resolveImageRemoval === 'function') {
      this.resolveImageRemoval();

      this.resolveImageRemoval = null;
      this.rejectImageRemoval = null;
    }
  };

  handleRemoveImageDialogClose = () => {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;

    dialog.setIsRemoveImageDialogOpen(false);

    if (typeof this.rejectImageRemoval === 'function') {
      this.rejectImageRemoval();

      this.resolveImageRemoval = null;
      this.rejectImageRemoval = null;
    }
  };

  closeModal = panelOption => {
    const {store} = this.props;
    const {feedbackDialog} = store;

    if (!feedbackDialog.canNavigateAway) {
      feedbackDialog.openDiscardDialog(() => {
        if (feedbackDialog.canNavigateAway) {
          this.props.popSidePanel();
        }
      });
      return false;
    } else {
      if (panelOption === false) {
        this.props.popSidePanel();
      }
      return true;
    }
  };

  openMediaUploaderDialog = () => {
    const {store} = this.props;
    const {checklist: checklistStore} = store;

    if (this.dropzoneRef) {
      this.dropzoneRef.open();
    }

    checklistStore.closeErrorModal();
  };

  setDropzoneRef = ref => {
    this.dropzoneRef = ref;
  };

  setMediaUploaderRef = ref => {
    this.mediaUploaderRef = ref;
  };

  componentDidUpdate() {
    const {store} = this.props;
    const {feedbackDialog: dialog} = store;
    const {canNavigateAway} = dialog;

    if (canNavigateAway != this.state.canNavigateAway) {
      this.setState({canNavigateAway});
      if (!canNavigateAway) {
        this.props.setLastPanelOptions({
          onClose: () => this.closeModal(true)
        });
      } else {
        this.props.setLastPanelOptions({
          onClose: undefined
        });
      }
    }
  }

  render() {
    const isDesktop = this.state.isDesktop;
    const {intl, store} = this.props;
    const {formatMessage} = intl;
    const {checklist: checklistStore, feedbackDialog: dialog} = store;
    const {
      feedbackText = '',
      isDiscardDialogOpen,
      isRemoveImageDialogOpen,
      isSaveWithoutNewImageDialogOpen,
      isTryAgainErrorDialogOpen,
      hasChanges,
      mediaUploadErrorText,
      mediaUrl
    } = dialog;

    return (
      <>
        <InfoModal
          isDesktop={isDesktop}
          isOpen={isDiscardDialogOpen}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={() => dialog.resolveCanDiscard(false)}
          onLeftButtonClick={() => dialog.resolveCanDiscard(false)}
          onRightButtonClick={() => dialog.resolveCanDiscard(true)}
          rightButtonText={formatMessage(messages.discard)}
          subtitle={formatMessage(messages.unsavedChangesSubtitle)}
          title={formatMessage(messages.unsavedChangesTitle)}
        />

        <InfoModal
          isDesktop={isDesktop}
          isOpen={isSaveWithoutNewImageDialogOpen}
          leftButtonText={formatMessage(messages.saveWithoutNewFile)}
          onClose={this.handleSaveWithoutNewImageDialogClose}
          onLeftButtonClick={this.handleSaveWithoutNewImageDialogConfirm}
          onRightButtonClick={this.handleSaveWithoutNewImageDialogClose}
          rightButtonText={formatMessage(messages.cancel)}
          subtitle={formatMessage(messages.fileStillUploadingSubtitle)}
          title={formatMessage(messages.fileStillUploadingTitle)}
        />

        <InfoModal
          isDesktop={isDesktop}
          isOpen={checklistStore.showErrorModal}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={checklistStore.closeErrorModal}
          onLeftButtonClick={checklistStore.closeErrorModal}
          onRightButtonClick={this.openMediaUploaderDialog}
          rightButtonText={formatMessage(messages.uploadNew)}
          subtitle={mediaUploadErrorText}
          title={formatMessage(messages.couldNotUploadFileTitle)}
        />

        <InfoModal
          isDesktop={isDesktop}
          isOpen={isTryAgainErrorDialogOpen}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={this.handleTryAgainErrorDialogClose}
          onLeftButtonClick={this.handleTryAgainErrorDialogClose}
          onRightButtonClick={this.handleTryAgainErrorDialogConfirm}
          rightButtonText={formatMessage(messages.tryAgain)}
          subtitle={formatMessage(messages.couldNotUploadFileSubtitle)}
          title={formatMessage(messages.couldNotUploadFileTitle)}
        />

        <InfoModal
          isDesktop={isDesktop}
          isOpen={isRemoveImageDialogOpen}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={this.handleRemoveImageDialogClose}
          onLeftButtonClick={this.handleRemoveImageDialogClose}
          onRightButtonClick={this.handleRemoveImageDialogConfirm}
          rightButtonText={formatMessage(messages.delete)}
          subtitle={formatMessage(messages.removeFileSubtitle)}
          title={formatMessage(messages.removeFileTitle)}
        />
        <Container>
          <ContentWrapper isDesktop={isDesktop}>
            <Description>
              <FormattedMessage {...messages.addFeedbackSubtitle} />
            </Description>

            <Label>{formatMessage(messages.details)}</Label>
            <ModalsTextBox
              isDesktop={isDesktop}
              handleTextareaChange={this.handleTextareaChange}
              placeholder={formatMessage(messages.typeYourComment)}
              value={feedbackText}
              maxFeedbackLength={maxFeedbackLength}
              dataCy={'feedback-text-check'}
            />
            <MediaUploaderWrapper isDesktop={isDesktop}>
              <MediaUploader
                dropzoneRef={this.setDropzoneRef}
                isDesktop={isDesktop}
                onUploadStart={this.handleMediaUploaderUploadStart}
                onUploadEnd={this.handleMediaUploaderUploadEnd}
                onBeforeRemove={this.handleMediaUploaderBeforeRemove}
                onChange={this.handleMediaUploaderChange}
                onError={this.handleMediaUploaderError}
                ref={this.setMediaUploaderRef}
                value={mediaUrl}
              />
            </MediaUploaderWrapper>
          </ContentWrapper>
          <ButtonsContainer>
            <Button
              aria-label="save"
              data-cy={'save-feedback-check'}
              onClick={this.handleFormSubmit}
              disabled={!hasChanges}
            >
              {formatMessage(messages.saveDetails)}
            </Button>
            <Button aria-label="cancel" design="outline" onClick={() => this.closeModal(false)}>
              {formatMessage(messages.cancel)}
            </Button>
          </ButtonsContainer>
        </Container>
      </>
    );
  }
}

export default injectIntl(CheckFeedbackModal);
